import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserCircleIcon,
  SignOutIcon,
  LockKeyIcon,
  TrashIcon,
  FilesIcon,
} from '@phosphor-icons/react';
import { useLogout, useDeleteAccount } from '../../services/auth.service';
import { ConfirmationModal } from '../ui/ConfirmationModal';

export const SidebarUserMenu = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const logoutMutation = useLogout();
  const deleteAccountMutation = useDeleteAccount();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    await deleteAccountMutation.mutateAsync();
    navigate('/login');
  };

  return (
    <>
      <div className="p-4 border-t border-gray-200 relative" ref={menuRef}>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="w-full flex items-center gap-3 px-4 py-3 text-text hover:bg-background rounded-lg transition"
        >
          <UserCircleIcon size={24} weight="regular" className="text-primary" />
          <span className="font-medium">{t('sidebar.my_account')}</span>
        </button>

        {isMenuOpen && (
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
            <button
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/change-password');
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-text hover:bg-background transition"
            >
              <LockKeyIcon size={20} weight="regular" className="text-primary" />
              <span className="font-medium">{t('sidebar.change_password')}</span>
            </button>
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={() => {
                setIsMenuOpen(false);
                navigate('/legal-documents');
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-text hover:bg-background transition"
            >
              <FilesIcon size={20} weight="regular" className="text-primary flex-shrink-0" />
              <span className="flex-1 text-left font-medium">{t('sidebar.legal_documents')}</span>
            </button>
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsDeleteModalOpen(true);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition"
            >
              <TrashIcon size={20} weight="regular" />
              <span className="font-medium">{t('sidebar.delete_account')}</span>
            </button>
            <button
              onClick={handleLogout}
              disabled={logoutMutation.isPending}
              className="w-full flex items-center gap-3 px-4 py-2 text-text hover:bg-background transition disabled:opacity-50"
            >
              <SignOutIcon size={20} weight="regular" className="text-primary" />
              <span className="font-medium">
                {logoutMutation.isPending ? t('sidebar.logging_out') : t('sidebar.logout')}
              </span>
            </button>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        title={t('sidebar.delete_account_modal.title')}
        message={t('sidebar.delete_account_modal.message')}
        confirmText={t('sidebar.delete_account_modal.confirm')}
        cancelText={t('sidebar.delete_account_modal.cancel')}
        isLoading={deleteAccountMutation.isPending}
        isDanger={true}
      />
    </>
  );
};
